import type {
	BackgroundJobOut,
	GroupItemSettingSchema,
} from '@repo/api-codegen';
import { debounce, sortBy } from 'lodash-es';
import { makeAutoObservable } from 'mobx';
import { createContext } from 'react';
import { VisibilityState } from './constants';

function getAllDescendants(
	groupItem: GroupItemSettingSchema
): GroupItemSettingSchema[] {
	return [groupItem, ...(groupItem.children || []).flatMap(getAllDescendants)];
}

export class SettingsStore {
	searchTerm: string = '';
	specType: 'builtin' | 'marketplace';
	initialSettings: GroupItemSettingSchema[] = [];
	expandedState: Record<string, boolean> = {};

	// Computed values
	settingsMap: Record<string, GroupItemSettingSchema> = {};
	filteredSettings: GroupItemSettingSchema[] = [];

	// Teams V2
	visibilityUpdates: Record<string, boolean> = {};
	teamsAssignedUpdates: Record<string, string[]> = {};

	// Teams V1
	settingDiff: Record<string, GroupItemSettingSchema> = {};

	backgroundJob: BackgroundJobOut | null = null;

	constructor(
		specType: 'builtin' | 'marketplace',
		initialSettings: GroupItemSettingSchema[]
	) {
		this.specType = specType;
		this.setInitialSettings(initialSettings);
		makeAutoObservable(this, {}, { autoBind: true });
	}

	setSearchTerm = debounce((searchTerm: string) => {
		this.searchTerm = searchTerm;
		this.computeFilteredSettings();
	}, 300);

	setInitialSettings(settings: GroupItemSettingSchema[]) {
		this.initialSettings = settings;

		const setSettings = (setting: GroupItemSettingSchema) => {
			this.settingsMap[setting.databuilder_id] = setting;
			setting.children?.forEach((child) => {
				setSettings(child);
			});
		};
		settings.forEach(setSettings);
		this.computeFilteredSettings();
	}

	computeFilteredSettings() {
		const filterGroupItems = (
			groupItems: GroupItemSettingSchema[],
			searchTerm: string
		): GroupItemSettingSchema[] => {
			const lowercasedSearchTerm = searchTerm.toLowerCase();
			const filteredItems = groupItems
				.filter((groupItem) => {
					const includesSearchTerm = groupItem.title
						.toLowerCase()
						.includes(lowercasedSearchTerm);

					if (includesSearchTerm) return true;

					if (!groupItem.children || groupItem.children.length === 0)
						return includesSearchTerm;

					const children = filterGroupItems(groupItem.children, searchTerm);
					return children.length > 0;
				})
				.map((groupItem) => {
					const includesSearchTerm = groupItem.title
						.toLowerCase()
						.includes(lowercasedSearchTerm);

					return includesSearchTerm
						? groupItem
						: {
								...groupItem,
								children: filterGroupItems(groupItem.children, searchTerm),
							};
				});
			return sortBy(filteredItems, ['native_type', 'title']);
		};

		this.filteredSettings = filterGroupItems(
			this.initialSettings,
			this.searchTerm
		);
	}

	isExpanded(databuilderId: string): boolean {
		return this.expandedState[databuilderId] ?? false;
	}

	setExpanded(databuilderId: string, expanded: boolean) {
		this.expandedState[databuilderId] = expanded;
	}

	areAllVisible(): VisibilityState {
		const settingsCount = this.filteredSettings.length;
		const visibleCount = this.filteredSettings.filter(
			(item) => this.isVisible(item.databuilder_id) === VisibilityState.VISIBLE
		).length;

		if (settingsCount === 0 || visibleCount === 0) {
			return VisibilityState.HIDDEN;
		}

		return visibleCount === settingsCount
			? VisibilityState.VISIBLE
			: VisibilityState.INDETERMINATE;
	}

	setAllVisible(visible: boolean) {
		this.filteredSettings.forEach((item) => {
			this.setVisible(item.databuilder_id, visible);
		});
	}

	isVisible(databuilderId: string): VisibilityState {
		if (this.specType === 'builtin') {
			const descendantIds = getAllDescendants(this.settingsMap[databuilderId])
				.filter(
					(d) =>
						d.databuilder_id !== this.settingsMap[databuilderId].databuilder_id
				)
				.map((d) => d.databuilder_id);

			if (descendantIds.length === 0) {
				return this.settingsMap[databuilderId].visible
					? VisibilityState.VISIBLE
					: VisibilityState.HIDDEN;
			}

			const allVisible = descendantIds.every(
				(descendantId) => this.settingsMap[descendantId].visible ?? true
			);
			const someVisible = descendantIds.some(
				(descendantId) => this.settingsMap[descendantId].visible ?? true
			);

			if (allVisible) {
				return VisibilityState.VISIBLE;
			}

			if (someVisible) {
				return VisibilityState.INDETERMINATE;
			}

			if (
				this.settingsMap[databuilderId].native_type === 'folder' ||
				this.settingsMap[databuilderId].native_type === 'space'
			) {
				return this.settingsMap[databuilderId].visible
					? VisibilityState.VISIBLE
					: VisibilityState.HIDDEN;
			}

			return VisibilityState.HIDDEN;
		}

		return this.settingsMap[databuilderId].visible
			? VisibilityState.VISIBLE
			: VisibilityState.HIDDEN;
	}

	setVisible(databuilderId: string, visible: boolean) {
		this.settingsMap[databuilderId].visible = visible;
		this.settingsMap[databuilderId].children?.forEach((child) => {
			this.setVisible(child.databuilder_id, visible);
		});
		this.settingDiff[databuilderId] = this.settingsMap[databuilderId];
		this.visibilityUpdates[databuilderId] = visible;
	}

	setCustomMappingTeams(databuilderId: string, teamIds: string[]) {
		this.settingsMap[databuilderId].mapping_teams = teamIds;
		this.settingDiff[databuilderId] = this.settingsMap[databuilderId];
		this.teamsAssignedUpdates[databuilderId] = teamIds;
	}

	resetDiff() {
		this.settingDiff = {};
	}

	resetTeamsAssignedUpdates() {
		this.teamsAssignedUpdates = {};
	}

	resetVisibilityUpdates() {
		this.visibilityUpdates = {};
	}

	setBackgroundJob(backgroundJob: BackgroundJobOut) {
		this.backgroundJob = backgroundJob;
	}

	resetBackgroundJob() {
		this.backgroundJob = null;
	}
}

export const SettingsStoreProvider = createContext<SettingsStore | null>(null);
